import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useRef, useState } from "react";
import Loader from "../../../components/Loader";
import PageTitleBar from "../../../components/PageTitleBar";
import { Button } from "../../../components/Button";
import { selectCurrentUserId } from "../../../store/models/User";
import Organization, {
  selectCurrentOrgId,
  selectFeaturesEnabled
} from "../../../store/models/Organization";
import _ from "lodash";
import Project, { selectOrgProjects } from "../../../store/models/Project";
import Select from 'react-select'
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import moment from 'moment';
import { DailyChart, StakedBarChart } from "../../../components/Chart";
import { createSelector } from "reselect";
import { Features } from "../../../constants";
import { selectMobileLayout } from "../../../store/UIState";
import { getDatesBetween } from "../../../utils";

const DEFAULT_OPTION = { label: 'All', value: 'all' };
export const UsagePage = () => {

  const dispatch = useDispatch();

  const userId = useSelector(state => selectCurrentUserId(state));
  const orgId = useSelector(state => selectCurrentOrgId(state));
  const projects = useSelector(state => selectOrgProjects(state, orgId));
  const featuresEnabled = useSelector(state => selectFeaturesEnabled(state));
  const projectOptions = [DEFAULT_OPTION].concat(projects.map(p => ({ label: p.name, value: p.id })))
  const [serviceOptions, setServiceOptions] = useState([DEFAULT_OPTION])
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState();
  const currentDate = new Date();
  const iniStartDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, currentDate.getDate());
  const [startDate, setStartDate] = useState(iniStartDate);
  const [endDate, setEndDate] = useState(currentDate);
  const [project, setProject] = useState(DEFAULT_OPTION);
  const [service, setService] = useState(DEFAULT_OPTION);
  const [isChanged, setIsChanged] = useState(false);
  const [dataKeys, setDataKeys] = useState([]);
  const [dataColors, setDataColors] = useState(['#18C99D', '#4271fc', '#e06ecd', '#fcee21', '#FFA500'
    , '#663399', '#a145ff']);
  const [sum, setSum] = useState(0);
  const chartOptions = [{ label: 'Resources', value: 'resources' },
  { label: 'Chatbot tokens', value: 'chatbot' },
  { label: 'Virtual machines', value: 'vm' },
  { label: 'Storage', value: 'storage' },
  { label: 'On-demand model APIs', value: 'ondemand' },
  // { label: 'I/Egress', value: 'iegress' }
  ];
  const [currentChart, setCurrentChart] = useState(chartOptions[0]);

  const dataColorsMap = {
    "VirtualMachines": '#18C99D',
    "BandwidthUsage": '#4271fc',
    "TranscodeVideo": '#e06ecd',
    "IngestStream": '#fcee21',
    "RAGChatbot": '#FFA500',
    "Storage": '#663399',
    'completion_tokens': '#18C99D',
    'embedding_tokens': '#4271fc',
    'prompt_tokens': '#e06ecd',
    "OndemandModelAPI": '#a145ff',
    "I/Egress": '#9be622',
    'vm_c1': '#18C99D'
  }
  const isMobile = useSelector(state => selectMobileLayout(state));
  const screenWidth = window.innerWidth;
  useEffect(() => {
    switch (currentChart.value) {
      case 'resources':
        fetchAiData();
        setProject(DEFAULT_OPTION);
        setService(DEFAULT_OPTION);
        return;
      case 'chatbot':
        fetchChatbotData();
        return;
      case 'vm':
        fetchVMData();
        return;
      case 'ondemand':
        fetchOndemandData();
        return;
      case 'storage':
        fetchStorageData();
        return;
      case 'iegress':
        fetchIegressData();
        return;
    }
  }, [orgId, currentChart.value]);

  const fetchVMData = async (service) => {
    setLoading(true);
    setError();
    try {
      let sDate = moment(startDate).format('YYYY-MM-DD');
      let eDate = moment(endDate).format('YYYY-MM-DD');
      let res = await dispatch(Organization.actions.fetchOrgVMUsage(orgId, sDate, eDate));
      let dates = getDatesBetween(moment(startDate, 'YYYY-MM-DD'), moment(endDate, 'YYYY-MM-DD'));
      const arr = [];
      const map = {}
      _.get(res, 'usageData').forEach(item => {
        let k = item.date.slice(0, 10);
        if (!map[k]) map[k] = {};
        map[k][item.service] = (Number(item.credit) / 100).toFixed(2);
      })
      let dks = _.get(res, 'serviceNames')
      setServiceOptions([{ label: 'All', value: 'all' }]
        .concat(dks.map(item => ({ label: item, value: item }))));
      setDataKeys(dks);
      let s = 0;
      dates.forEach(date => {
        let mapObj = map[date];
        let newObj = { name: date };
        if (service) {
          newObj[service] = mapObj ? (Number(mapObj[service]) || 0) : 0;
          s += newObj[service];
        } else {
          dks.forEach(k => {
            newObj[k] = mapObj ? (Number(mapObj[k]) || 0) : 0;
            s += newObj[k];
          })
        }
        arr.push(newObj)
      });
      setSum(s);
      setData(arr);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setIsChanged(false);
    }
  }

  const fetchChatbotData = async () => {
    setLoading(true);
    setError();
    try {
      let sDate = moment(startDate).format('YYYY-MM-DD');
      let eDate = moment(endDate).format('YYYY-MM-DD');
      let res = await dispatch(Organization.actions.fetchOrgChatbotUsage(orgId, sDate, eDate));
      let dates = getDatesBetween(moment(startDate, 'YYYY-MM-DD'), moment(endDate, 'YYYY-MM-DD'));
      const arr = [];
      const map = {};
      res.forEach(item => {
        let k = item.day.slice(0, 10);
        if (!map[k]) map[k] = {};
        map[k] = item;
      })
      const dks = ['completion_tokens', 'embedding_tokens', 'prompt_tokens']
      setDataKeys(dks);
      setDataColors(dks.map(dk => dataColorsMap[dk]))
      let s = 0;
      dates.forEach(date => {
        let mapObj = map[date];
        let newObj = { name: date };
        dks.forEach(k => {
          newObj[k] = mapObj ? (Number(mapObj[k]) || 0) : 0;
          s += newObj[k];
        })
        arr.push(newObj)
      });
      setSum(s);
      setData(arr);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setIsChanged(false);
    }
  }
  const fetchAiData = async (project, service) => {
    setLoading(true);
    setError();
    try {
      let sDate = moment(startDate).format('YYYY-MM-DD')
      let eDate = moment(endDate).format('YYYY-MM-DD')
      await dispatch(Project.actions.fetchUserProjectsInOrg(orgId));
      let filter, filterType;
      if (project) {
        filter = project;
        filterType = 'project'
      }
      let res = await dispatch(Organization.actions.fetchOrgUsage(orgId, sDate, eDate, filter, filterType));

      let dates = getDatesBetween(moment(startDate, 'YYYY-MM-DD'), moment(endDate, 'YYYY-MM-DD'));
      const arr = [];
      const map = {}
      _.get(res, 'usageData').forEach(item => {
        let k = item.date.slice(0, 10);
        if (!map[k]) map[k] = {};
        map[k][item.service] = (Number(item.credit) / 100).toFixed(2);
      })
      let dks = _.get(res, 'serviceNames')
      setServiceOptions([{ label: 'All', value: 'all' }]
        .concat(dks.map(item => ({ label: item, value: item }))));
      if (service) {
        setDataKeys([service]);
        setDataColors([dataColorsMap[service]])
      } else {
        setDataKeys(dks);
        setDataColors(dks.map(dk => dataColorsMap[dk]));
      }
      let s = 0;
      dates.forEach(date => {
        let mapObj = map[date];
        let newObj = { name: date };
        if (service) {
          newObj[service] = mapObj ? (Number(mapObj[service]) || 0) : 0;
          s += newObj[service];
        } else {
          dks.forEach(k => {
            newObj[k] = mapObj ? (Number(mapObj[k]) || 0) : 0;
            s += newObj[k];
          })
        }
        arr.push(newObj)
      });
      setSum(s);
      setData(arr);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setIsChanged(false);
    }
  }
  const fetchOndemandData = async (service) => {
    setLoading(true);
    setError();
    try {
      let sDate = moment(startDate).format('YYYY-MM-DD');
      let eDate = moment(endDate).format('YYYY-MM-DD');
      let res = await dispatch(Organization.actions.fetchOrgOndemandUsage(orgId, sDate, eDate));
      let dates = getDatesBetween(moment(startDate, 'YYYY-MM-DD'), moment(endDate, 'YYYY-MM-DD'));
      const arr = [];
      const map = {}
      _.get(res, 'usageData').forEach(item => {
        let k = item.date.slice(0, 10);
        if (!map[k]) map[k] = {};
        map[k][item.service] = (Number(item.credit) / 100).toFixed(2);
      })
      let dks = _.get(res, 'serviceNames')
      setServiceOptions([{ label: 'All', value: 'all' }]
        .concat(dks.map(item => ({ label: item, value: item }))));

      setDataKeys(dks);

      let s = 0;
      dates.forEach(date => {
        let mapObj = map[date];
        let newObj = { name: date };
        if (service) {
          newObj[service] = mapObj ? (Number(mapObj[service]) || 0) : 0;
          s += newObj[service];
        } else {
          dks.forEach(k => {
            newObj[k] = mapObj ? (Number(mapObj[k]) || 0) : 0;
            s += newObj[k];
          })
        }
        arr.push(newObj)
      });
      setSum(s);
      setData(arr);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setIsChanged(false);
    }
  }

  const fetchStorageData = async (service) => {
    setLoading(true);
    setError();
    try {
      let sDate = moment(startDate).format('YYYY-MM-DD');
      let eDate = moment(endDate).format('YYYY-MM-DD');
      let res = await dispatch(Organization.actions.fetchOrgStorageQuotaUsage(orgId, sDate, eDate));
      let dates = getDatesBetween(moment(startDate, 'YYYY-MM-DD'), moment(endDate, 'YYYY-MM-DD'));
      const arr = [];
      const map = {}
      _.get(res, 'usageData').forEach(item => {
        let k = item.date.slice(0, 10);
        if (!map[k]) map[k] = {};
        map[k][item.service] = (Number(item.credit) / 100).toFixed(2);
      })
      let dks = _.get(res, 'serviceNames')
      setServiceOptions([{ label: 'All', value: 'all' }]
        .concat(dks.map(item => ({ label: item, value: item }))));

      setDataKeys(dks);

      let s = 0;
      dates.forEach(date => {
        let mapObj = map[date];
        let newObj = { name: date };
        if (service) {
          newObj[service] = mapObj ? (Number(mapObj[service]) || 0) : 0;
          s += newObj[service];
        } else {
          dks.forEach(k => {
            newObj[k] = mapObj ? (Number(mapObj[k]) || 0) : 0;
            s += newObj[k];
          })
        }
        arr.push(newObj)
      });
      setSum(s);
      setData(arr);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setIsChanged(false);
    }
  }

  const fetchIegressData = async (service) => {
    setLoading(true);
    setError();
    try {
      let sDate = moment(startDate).format('YYYY-MM-DD');
      let eDate = moment(endDate).format('YYYY-MM-DD');
      let res = await dispatch(Organization.actions.fetchOrgIegressUsage(orgId, sDate, eDate));
      console.log('res:', res)
      let dates = getDatesBetween(moment(startDate, 'YYYY-MM-DD'), moment(endDate, 'YYYY-MM-DD'));
      const arr = [];
      const map = {}
      _.get(res, 'usageData').forEach(item => {
        let k = item.date.slice(0, 10);
        if (!map[k]) map[k] = {};
        map[k][item.service] = (Number(item.credit) / 100).toFixed(2);
      })
      let dks = _.get(res, 'serviceNames')
      setServiceOptions([{ label: 'All', value: 'all' }]
        .concat(dks.map(item => ({ label: item, value: item }))));

      setDataKeys(dks);

      let s = 0;
      dates.forEach(date => {
        let mapObj = map[date];
        let newObj = { name: date };
        if (service) {
          newObj[service] = mapObj ? (Number(mapObj[service]) || 0) : 0;
          s += newObj[service];
        } else {
          dks.forEach(k => {
            newObj[k] = mapObj ? (Number(mapObj[k]) || 0) : 0;
            s += newObj[k];
          })
        }
        arr.push(newObj)
      });
      setSum(s);
      setData(arr);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setIsChanged(false);
    }
  }

  const handleChange = (v, type) => {
    if (type === 'project') {
      setProject(v)
    } else if (type === 'service') {
      setService(v)
    }
    if (!isChanged) setIsChanged(true);
  }

  const handleUpdate = async () => {
    if (currentChart.value === 'resources') {
      let p = project.value === 'all' ? null : project.value;
      let s = service.value === 'all' ? null : service.value;
      fetchAiData(p, s);
      return;
    }
    if (currentChart.value === 'chatbot') {
      fetchChatbotData();
      return;
    }
    if (currentChart.value === 'vm') {
      fetchVMData();
      return;
    }
  }

  const handleDownload = () => {
    function convertToCSV(objArray) {
      var array = typeof objArray != 'object' ? JSON.parse(objArray) : objArray;
      var str = '';
      var line = '';
      for (var index in array[0]) {
        if (line != '') line += ','
        line += index;
      }
      str += line + '\r\n';
      for (var i = 0; i < array.length; i++) {
        var line = '';
        for (var index in array[i]) {
          if (line != '') line += ','

          line += array[i][index];
        }

        str += line + '\r\n';
      }
      return str;
    }


    var csv = convertToCSV(data.map(item => {
      const { name, ...rest } = item;

      let renamed = rest;

      if (currentChart.value === 'resources') {
        renamed = Object.keys(rest).reduce((acc, key) => {
          acc[`${key}($)`] = rest[key];
          return acc;
        }, {});
      }

      return {
        date: name,
        ...renamed
      };
    }));

    var blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', `${orgId}_usage.csv`);
    a.click();
    window.URL.revokeObjectURL(url);
  }


  if (error)
    return (<div className={"ServicePage__Error"}>
      {error}
    </div>)

  console.log('data:', data)

  return (<div className={"UsagePage"}>
    <PageTitleBar alignTitleLeft={true} title={"Usage"}
      rightContent={<Select options={chartOptions}
        className={"UsagePage__selector"}
        styles={{
          ...selectStyles,
          singleValue: (styles, { data }) => ({ ...styles, color: 'white', fontWeight: '500' }),
        }}
        placeholder={'Select a chart'}
        value={currentChart}
        onChange={(v) => { setCurrentChart(v) }}
      />} />

    <div className="UsagePage__content">
      <div className="UsagePage__chart">
        {loading
          ? <Loader color='green' size='large' />
          : data
            ? dataKeys.length > 0
              ? <div className="UsagePage__chart--container">
                <StakedBarChart data={data}
                  width={isMobile ? screenWidth - 40 : 650}
                  height={400}
                  legendName={getLegendName(currentChart.value)}
                  dataKeys={dataKeys}
                  dataColors={dataColors} />
                <div className="UsagePage__chart--tooltip">
                  {getChartToolTips(currentChart.value, sum)}
                </div>
              </div>
              : <div className="UsagePage__chart--note">
                No data available in the selected date range
              </div>
            : <div className="UsagePage__chart--note">
              Select a project to get started
            </div>}
      </div>
      <div className="UsagePage__selector-wrap">
        {currentChart.value === 'resources' && <>
          <div className="UsagePage__selector-label">Project</div>
          <Select options={projectOptions}
            className={"UsagePage__selector"}
            styles={selectStyles}
            placeholder={'Select a project'}
            value={project}
            onChange={(v) => handleChange(v, 'project')}
          />
          <div className="UsagePage__selector-label">Service</div>
          <Select options={serviceOptions}
            className={"UsagePage__selector"}
            styles={selectStyles}
            placeholder={'Select a service'}
            value={service}
            onChange={(v) => handleChange(v, 'service')}
          />
        </>}
        <div className="UsagePage__selector-label top">Start Date</div>
        <DatePicker selected={startDate}
          onChange={(v) => {
            setStartDate(v);
            if (!isChanged) setIsChanged(true)
          }}
          disabled={loading}
          maxDate={endDate} />
        <div className="UsagePage__selector-label">End Date</div>
        <DatePicker selected={endDate}
          onChange={(v) => {
            setEndDate(v)
            if (!isChanged) setIsChanged(true)
          }}
          disabled={loading}
          minDate={startDate}
          maxDate={currentDate} />
        <Button className={"UsagePage__button"}
          color={"green"}
          loading={loading}
          title={isChanged
            ? "Update Chart"
            : "Download Data"}
          onClick={isChanged
            ? handleUpdate
            : handleDownload}
          disabled={dataKeys.length === 0} />

      </div>
    </div >
  </div >
  )
}

const getChartToolTips = (type, sum) => {
  switch (type) {
    case 'resources':
    case 'vm':
    case 'ondemand':
    case 'storage':
      return `Total credit consumed within the selected date range: $${sum.toFixed(2)}`;
    case 'chatbot':
      return `Total chatbot tokens consumed within the selected date range: ${sum}`;
    case 'iegress':
      return `Total data ingress and egress during the selected date range: ${sum}`;
    default:
      return;
  }
}

const getLegendName = (type) => {
  switch (type) {
    case 'resources':
      return `Usage`;
    case 'chatbot':
      return `Token Usage`;
    case 'vm':
      return `Virtual Machine Usage`;
    case 'ondemand':
      return `On-demand model API Usage`;
    case 'storage':
      return `Storage Usage`;
    case 'iegress':
      return `I/Egress`;
    default:
      return '';
  }
}

const selectStyles = {
  container: styles => ({
    ...styles,
    flex: 1,
    maxWidth: '90%',
    height: 40,
  }),
  control: (styles, { isFocused }) => ({
    ...styles,
    paddingLeft: 15,
    backgroundColor: '#191D29',
    borderColor: '#3D4463',
    borderRadius: 6,
    boxShadow: isFocused ? '0 0 0 1px #18c99d' : 'none',
    color: '#18c99d',
    ':hover': {
      borderColor: '#18c99d',
      boxShadow: '0 0 0 1px #18c99d',
    },
    '.selected': {
      borderColor: '#18c99d',
      boxShadow: '0 0 0 1px #18c99d',
    }
  }),
  menu: (styles, state) => ({
    ...styles,
    border: '1px solid #3D4463',
    backgroundColor: '#191D29',
  }),
  option: (styles, { data, isDisabled, isFocused, isSelected }) => {
    return {
      ...styles,
      backgroundColor: '#191D29',
      borderColor: '#3D4463',
      color: isDisabled ? '#636B91' : (isFocused ? '#18c99d' : (isSelected ? '#18c99d' : '#636B91')),
      opacity: isDisabled ? 0.5 : 1,
    };
  },
  indicatorsContainer: (styles, { isDisabled }) => ({
    ...styles,
    display: isDisabled ? 'none' : 'flex',
  }),
  dropdownIndicator: (styles, state) => ({
    ...styles,
    color: '#8A8FB5',
  }),
  indicatorSeparator: (styles, state) => ({
    ...styles,
    color: '#636B91',
    backgroundColor: '#636B91',
    display: 'none'
  }),
  input: styles => ({ ...styles }),
  placeholder: styles => ({ ...styles, color: '#636B91' }),
  singleValue: (styles, { data }) => ({ ...styles, color: '#636B91', fontWeight: '500' }),
};